import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {authentication} from "../../../../../authentication";
import {Queue} from "../../../../../generated";
import {backend} from "../../../../../xconvert-backend";
import {ConfigContext} from "../../../context/ConfigContext";
import {clampTabIndex} from "../../../context/TabSession";
import ReactTable from "react-table";
import {Button, ButtonGroup, Icon, Label, Popup, Tab} from "semantic-ui-react";
import AceEditor from 'react-ace';

import 'brace/mode/groovy'
import 'brace/ext/searchbox'
import Notifications, {notify} from 'react-notify-toast';
import {IfBox} from "../../../../style/if";
import {ScriptModal} from "../../configChange/configEdit/modals/ScriptModal";
import {TestSuppressionScriptModal} from "./TestSuppressionScript/TestSuppressionScriptModal";
import {Splitter} from "../../../../util/table-splitter-ui/src/components/Splitters/Splitter";
import {QueueMask} from "./QueueMask";

export interface QueuesEditPageProps {
    history: any
}

export function QueuesEditPage(props: QueuesEditPageProps) {
    const context = useContext(ConfigContext);

    const [isLoading, setIsLoading] = useState(false);
    const [take] = useState(20);
    const [page] = useState(1);
    const [count, setCount] = useState(0);
    const [queues, setQueues] = useState<Queue[]>([]);
    const [selectedQueue, setSelectedQueue] = useState<Queue | null>(null);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [workingCopy, setWorkingCopy] = useState<string>("");
    const [validJson, setValidJson] = useState(true);
    const [openScriptModal, setOpenScriptModal] = useState(false);
    const [suppressionScript, setSuppressionScript] = useState<string>("");
    const [openTestModal, setOpenTestModal] = useState(false);
    const [savable, setSavable] = useState(false);

    useEffect(() => {
        loadQueues().then();
    }, []);

    useEffect(() => {
        let workingCopyValue = context.queueWorkingCopies?.find(c => c.id === context.selectedQueueId)?.json;

        setSavable(!!workingCopyValue);
        setValidJson(true);
        let index = queues.findIndex(q => q._id === context.selectedQueueId);
        setSelectedIndex(index);
        setSuppressionScript(getSuppressionScript(workingCopyValue || ""));
    }, [queues, context.selectedQueueId]);

    useEffect(() => {
        let index = queues.findIndex(q => q._id === context.selectedQueueId);
        if (selectedIndex !== index) {
            let wc = context.queueWorkingCopies?.find(c => c.id === context.selectedQueueId)?.json;
            let selectedQueueObj = queues.find(c => c._id === context.selectedQueueId);
            let selectedQueueStr = selectedQueueObj ? JSON.stringify(selectedQueueObj, null, 4) : null;
            if (!wc) wc = selectedQueueStr;
            setSelectedIndex(index);
            setWorkingCopy(wc || "");
            setSelectedQueue(selectedQueueObj || null);
        }
        let newSupScript = getSuppressionScript(workingCopy);
        if (newSupScript !== suppressionScript) {
            setSuppressionScript(newSupScript);
        }
    }, [context.selectedQueueId, queues, selectedIndex, workingCopy, suppressionScript]);

    async function loadQueues() {
        setIsLoading(true);
        let auth = backend.withTokenAuthHeader(authentication.token);
        let resp = await backend.queueApi.fetchQueueList(context.companyId, auth);
        setQueues(resp.queues);
        setCount(resp.queues.length);
        setIsLoading(false);
    }

    async function onSave() {
        let auth = backend.withTokenAuthHeader(authentication.token);
        let newQueue = JSON.parse(workingCopy);
        let response = await backend.queueApi.updateQueue(newQueue._id, newQueue, auth);

        if (response) {
            notify.show('updated comment', 'success', 3000, '#28f751');
            await loadQueues();
        } else {
            notify.show("ERROR: unable to change comment.", 'error', 5000, '#fc0303');
        }
    }

    function onDiscard() {
        handleChange(JSON.stringify(selectedQueue, null, 4))
    }

    function replaceSuppressionScript(script: string | null) {
        let jp = require('jsonpath');
        let jsonPath = '$.suppressionRules.suppressionScript';
        let workingCopyObj = JSON.parse(workingCopy);
        jp.value(workingCopyObj, jsonPath, script);
        let newWorkingCopyStr = JSON.stringify(workingCopyObj, null, 4);

        handleChange(newWorkingCopyStr);
        setOpenScriptModal(false);
    }

    function getSuppressionScript(wc?: string) {
        try {
            let jp = require('jsonpath');
            let jsonPath = '$.suppressionRules.suppressionScript';
            let workingCopyObj = JSON.parse(wc ?? workingCopy);
            return jp.query(workingCopyObj, jsonPath)[0] || "";
        } catch {
            return "";
        }
    }

    function handleChange(newWorkingCopy: string) {
        setWorkingCopy(newWorkingCopy);
        try {
            let parsed = JSON.parse(newWorkingCopy);
            setValidJson(true);
            setSuppressionScript(getSuppressionScript(newWorkingCopy));
            if (newWorkingCopy === JSON.stringify(selectedQueue, null, 4)) {
                let newArray = context.queueWorkingCopies.filter(c => c.id !== parsed._id);
                context.setQueueWorkingCopies(newArray);
                setSavable(false);
            } else {
                context.updateQueueWorkingCopy({id: parsed._id, json: newWorkingCopy});
                setSavable(true);
            }
        } catch {
            setValidJson(false);
        }
    }

    function getWorkingCopyAsObject() {
        try {
            return JSON.parse(workingCopy);
        } catch {
            return {};
        }
    }

    function changed(d: any) {
        if (context.queueWorkingCopies.find(c => c.id === d._id)) {
            return (
                <Popup
                    trigger={
                        <Icon
                            name={"warning sign"}
                            color={"yellow"}
                            size='large'
                        />
                    }
                >
                    There are unsaved changes on this Queue!
                </Popup>
            );
        }
        return <></>;
    }

    function renderTable() {
        const columns = [
            {
                id: 'Changed',
                Header: '',
                width: 50,
                accessor: (d: any) => changed(d),
                sortField: 'CHANGE'
            },
            {
                id: 'name',
                Header: 'name',
                width: 250,
                accessor: (d: any) => d.name,
                sortField: 'NAME'
            }, {
                id: '_id',
                Header: 'ID',
                width: 200,
                accessor: (d: any) => d._id,
                sortField: 'ID'
            }, {
                id: 'type',
                Header: 'type',
                width: 200,
                accessor: (d: any) => d.type,
                sortField: 'TYPE'
            }, {
                id: 'enabled',
                Header: 'enabled',
                width: 100,
                accessor: (d: any) => d.enabled !== false
                    ? <Icon name='check' color='green' size='large'/>
                    : <Icon name='x' color='red' size='large'/>,
                sortField: 'ENABLED'
            }, {
                id: 'mirroringEnabled',
                Header: 'mirroringEnabled',
                width: 150,
                accessor: (d: any) => d.mirroringEnabled
                    ? <Icon name='check' color='green' size='large'/>
                    : <Icon name='x' color='red' size='large'/>,
                sortField: 'MIRRORING_ENABLED'
            }
        ];

        if (queues == null) {
            return <p>Loading</p>;
        }

        return (
            <ReactTable
                data={queues}
                pages={Math.ceil(count / take)}
                columns={columns}
                sorted={[
                    {
                        id: 'NAME',
                        desc: false
                    }
                ]}
                defaultPageSize={take}
                className="-striped -highlight"
                loading={isLoading}
                getTdProps={(state, rowInfo) => {
                    return {
                        onClick: () => {
                            let savableValue = true;
                            let wc = context.queueWorkingCopies?.find(c => c.id === rowInfo.row._original._id)?.json;
                            if (!wc) {
                                wc = JSON.stringify(rowInfo.row._original, null, 4);
                                savableValue = false;
                            }
                            setSelectedIndex(rowInfo.index);
                            setSelectedQueue(rowInfo.row._original);
                            setWorkingCopy(wc);
                            setSavable(savableValue);
                            context.setSelectedQueueId(rowInfo.row._original._id);
                        },
                        style: {
                            background: rowInfo?.index === selectedIndex ? '#00afec' : 'white',
                            color: rowInfo?.index === selectedIndex ? 'white' : 'black'
                        }
                    };
                }}
                style={{cursor: "pointer"}}
            />
        );
    }

    function tabView() {
        const panes = [
            {
                menuItem: "Mask",
                render: () => <Tab.Pane>{
                    <QueueMask
                        queueStr={workingCopy}
                        onChange={(newQueue) => handleChange(JSON.stringify(newQueue, null, 4))}
                    />
                }
                </Tab.Pane>,
            },
            {
                menuItem: "Json",
                render: () => <Tab.Pane>{jsonEditor()}</Tab.Pane>,
            }]

        return <Tab
            id={"QueueEditTabView"}
            panes={panes}
            className={"flexTab"}
            defaultActiveIndex={clampTabIndex(context.tabSession.queueEditTab, panes)}
            onTabChange={(_, data) => {
                context.setSingleTabSession("queueEditTab", parseInt(data.activeIndex.toString()))
            }}
            menu={{secondary: true, pointing: true, style: {flexShrink: 0, borderBottom: '1px solid #ddd'}}}
        />
    }

    function jsonEditor() {
        return <AceEditor
            theme="monokai"
            style={{flex: 1}}
            mode='groovy'
            value={workingCopy}
            placeholder='Message'
            width="100%"
            readOnly={false}
            onChange={handleChange}
        />
    }

    function drawEditor() {
        return (
            <>
                {tabView()}
                <div>
                    <IfBox shouldShow={!validJson}>
                        <Label size={'large'} color='red'>INVALID JSON</Label>
                    </IfBox>
                    <ButtonGroup>
                        <Button
                            disabled={!validJson || !savable}
                            color={"green"}
                            onClick={onSave}
                        >
                            Save
                        </Button>
                        <Button
                            disabled={selectedQueue == null}
                            onClick={() => setOpenScriptModal(true)}
                        >
                            Edit SuppressionScript
                        </Button>
                        <Button
                            disabled={selectedQueue == null || !validJson}
                            onClick={() => setOpenTestModal(true)}
                        >
                            Test SuppressionScript
                        </Button>
                        <Button
                            disabled={!validJson || !savable}
                            color={"red"}
                            onClick={onDiscard}
                        >
                            Discard
                        </Button>
                    </ButtonGroup>
                    {openScriptModal &&
                        <ScriptModal
                            isOpen={openScriptModal}
                            onClose={() => setOpenScriptModal(false)}
                            script={suppressionScript}
                            dispatcher={replaceSuppressionScript}
                            saveKeyListener={null}
                            path={"suppressionRules.suppressionScript"}
                            readOnly={false}
                            fieldDescriptionEnabled={false}
                            defaultDescription={""}
                        />
                    }
                    {openTestModal &&
                        <TestSuppressionScriptModal
                            isOpen={openTestModal}
                            onClose={() => setOpenTestModal(false)}
                            queue={getWorkingCopyAsObject()}
                            history={props.history}
                        />
                    }
                </div>
            </>
        );
    }

    return <>
        <Splitter
            name={"queuesEditPageSplitter"}
            left={renderTable()}
            right={drawEditor()}
        />
        <Notifications/>
    </>
}